import { FC, useEffect, useState } from 'react';
import { mainColors } from '../../../../../styling/theme';
import KidsEditorGridItem from '../../kidsV2/layout/KidsEditorGridItem.component';
import { pdfjs } from 'react-pdf';
import useRouteParams from '../../../../../hooks/useRouteParams';
import { useDispatch } from 'react-redux';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../../redux/ui/controlbar/actions';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import ReviewConfirmationDialogBox from '../../components/buttons/ReviewConfirmationDialogBox.component';
import { Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BackToOverviewBtn from '../../components/buttons/BackToOverviewBtn';
import { useRaidrKids } from '../context/RaidrKidsContext';
import { useKIDsOverviewData, useKIDsReviewData } from '../services/queries';
import { useGenerateNewReviewKIDPdf } from '../services/mutations';
import { useRaidrViewStyles } from '../styles/Raidr.styles';
import ControlBarOptions from '../../kidsV2/layout/ControlBarOptions.component';
import TokenService from '../../../../../services/token-service';
import RaptorPDFViewer from '../../../../general/raptorPDFViewer/RaptorPDFViewer';
import { getOS } from '../../../../../utilities/helpers/common/system';

const KidsReview: FC = () => {
  const classes = useRaidrViewStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user_client_id: clientId } = TokenService.getUser()!;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const OS = getOS();
  const [hasError, setHasError] = useState(false);

  const { isPriips, backToOverviewLink, hasEditPermissions, iframeTitle } =
    useRaidrKids();

  const {
    data: KIDsOverviewData,
    isPending,
    error,
  } = useKIDsOverviewData(clientId);

  const createNewReviewKIDPdf = useGenerateNewReviewKIDPdf();

  const [reviewContentData, setReviewContentData] = useState<any[]>([]);

  // Variable used to display the confirm review dialog box.
  const [confirmReviewIsShowing, setConfirmReviewIsShowing] =
    useState<boolean>(false);

  // Get route params
  const fundId = useRouteParams('fundId')['fundId'];
  const isin = useRouteParams('isin')['isin'];
  const documentLanguage =
    useRouteParams('documentLanguage')['documentLanguage'];
  const currentVersion = useRouteParams('currentVersion')['currentVersion'];
  const publishedVersion =
    useRouteParams('publishedVersion')['publishedVersion'];

  const { data } = useKIDsReviewData(
    clientId,
    fundId,
    isin,
    currentVersion,
    publishedVersion,
    documentLanguage,
  );

  useEffect(() => {
    if (data?.data) setReviewContentData(data?.data);
  }, [data?.data]);

  const getPDF = async () => {
    // Check if the required fields have been filled in.
    if (!reviewContentData || !reviewContentData.length) {
      return;
    }

    try {
      // Create an object of formData
      const formData = new FormData();
      // Convert the content to json
      const kidsJson = JSON.stringify(reviewContentData);
      // Update the formData object.
      formData.append('contents', kidsJson);
      formData.append('params', JSON.stringify(['contents']));

      const response = await createNewReviewKIDPdf.mutateAsync(formData);

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(blob);

      const iframe = document.querySelector('iframe');
      if (iframe?.src) {
        iframe.src = fileUrl;
        iframe.title = iframeTitle;
      }

      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      // Setting the worker for pdfjs:
      if (OS != 'linux') {
        pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
      }

      setPdfUrl(fileUrl);
    } catch (error) {
      console.error('Failed to refresh PDF:', error);
      setHasError(true);
    }
  };

  useEffect(() => {
    if (reviewContentData.length) {
      getPDF();
    }
  }, [reviewContentData]);

  useEffect(() => {
    return () => {
      dispatch(toggleControlBarShowing(false));
    };
  }, [dispatch]);

  // Toggle the control bar to be shown
  useEffect(() => {
    if (reviewContentData.length > 0) {
      dispatch(
        updateControlBarComponents([
          <BackToOverviewBtn
            backToOverview={() =>
              navigate(backToOverviewLink, { state: { fundId, isin } })
            }
            dontSetPosition
            key="back_to_overview"
          />,
          <ControlBarOptions
            title={`${reviewContentData[0].target[0].fund_name} - ${reviewContentData[0].target[0].share_class}`}
          >
            {hasEditPermissions && (
              <Tooltip
                placement="top"
                enterDelay={1000}
                classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
                arrow
                title="Go to Edit page for this document."
              >
                <Button
                  variant="contained"
                  sx={{
                    fontSize: '0.94rem',
                    minWidth: '160px',
                    color: 'white',
                    backgroundColor: mainColors.mainBlue,
                    borderRadius: '0.12rem',
                    padding: '6px 16px',
                    height: '36px',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: mainColors.mainBlue_lighter,
                    },
                    fontWeight: 600,
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                  }}
                  disableElevation={true}
                  onClick={() =>
                    navigate(
                      isPriips
                        ? `/raidr/priips-kids-editor?fundId=${fundId}&isin=${isin}&documentLanguage=${documentLanguage}`
                        : `/raidr/ucits-kiids-editor?fundId=${fundId}&isin=${isin}&documentLanguage=${documentLanguage}`,
                    )
                  }
                  endIcon={
                    <EditIcon
                      sx={{
                        fontSize: '1.25rem',
                      }}
                    />
                  }
                >
                  Edit Document
                </Button>
              </Tooltip>
            )}
            <Tooltip
              placement="top"
              enterDelay={1000}
              classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
              arrow
              title="Confirm Review"
            >
              <Button
                variant="contained"
                sx={{
                  fontSize: '0.94rem',
                  minWidth: '180px',
                  color: 'white',
                  backgroundColor: mainColors.mainBlue,
                  borderRadius: '0.12rem',
                  padding: '6px 16px',
                  height: '36px',
                  marginLeft: '10px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: mainColors.mainBlue_lighter,
                  },
                  fontWeight: 600,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                }}
                disableElevation={true}
                onClick={() => setConfirmReviewIsShowing(true)}
                endIcon={
                  <CheckIcon
                    sx={{
                      fontSize: '1.25rem',
                    }}
                  />
                }
              >
                Complete Review
              </Button>
            </Tooltip>
          </ControlBarOptions>,
        ]),
      );
      dispatch(toggleControlBarShowing(true));
    }
  }, [reviewContentData, KIDsOverviewData]);

  return (
    <>
      <KidsEditorGridItem xs={12}>
        <RaptorPDFViewer
          pdfUrl={pdfUrl}
          fileName={iframeTitle}
          hasError={hasError}
          errorMessage={''}
          setHasError={setHasError}
          isPending={createNewReviewKIDPdf.isPending}
        />
      </KidsEditorGridItem>
      <KidsEditorGridItem xs={12}>
        {confirmReviewIsShowing && (
          <ReviewConfirmationDialogBox
            setDialogIsShowing={setConfirmReviewIsShowing}
            dialogIsShowing={confirmReviewIsShowing}
            fundId={fundId || ''}
            shareClass={isin || ''}
            documentLanguage={documentLanguage || ''}
            overviewData={KIDsOverviewData || []}
            backToOverviewLink={backToOverviewLink}
            hasEditBtn={isPriips}
            isPriips={isPriips}
          />
        )}
      </KidsEditorGridItem>
    </>
  );
};

export default KidsReview;
